import type { TAsyncState } from "./types";

export type TStateAsyncSlice<TSliceName extends string, TData, TError> = Record<
    TSliceName,
    TAsyncState<TData, TError>
>;

type TActionsPublicAsyncSlice<
    TSliceName extends string,
    TData,
    TParams extends unknown[] | [] = [],
> = Record<
    `run${Capitalize<TSliceName>}`,
    (...args: TParams) => Promise<TData>
>;

export type TAsyncSlice<
    TSliceName extends string,
    TData,
    TError,
    TParams extends unknown[] | [] = [],
> = TStateAsyncSlice<TSliceName, TData, TError> &
    TActionsPublicAsyncSlice<TSliceName, TData, TParams>;

type TCreateAsyncSliceConfig<
    TSliceName extends string,
    TData,
    TError,
    TParams extends unknown[] | [] = [],
> = {
    name: TSliceName;
    asyncFn: (...args: TParams) => Promise<TData>;
    rethrowError?: boolean;
    initialState?: TStateAsyncSlice<TSliceName, TData, TError>;
};

export const createAsyncSlice =
    <
        TSliceName extends string,
        TData,
        TError,
        TParams extends unknown[] | [] = [],
    >({
        name,
        asyncFn,
        rethrowError,
        initialState,
    }: TCreateAsyncSliceConfig<TSliceName, TData, TError, TParams>) =>
    (
        set: <TName extends string>(
            statePartial: TStateAsyncSlice<TSliceName, TData, TError>,
            replace?: boolean,
            action?: TName,
        ) => void,
    ): TAsyncSlice<TSliceName, TData, TError, TParams> => {
        if (typeof set !== "function") {
            throw new Error(
                `[createAsyncSlice] Expected 'set' to be a function in slice '${name}', but received ${typeof set}`,
            );
        }

        if (set.length === 0) {
            throw new Error(
                `[createAsyncSlice] Type error in slice '${name}': Expected 'set' to be a function with parameters, but received a parameter-less function. ` +
                    `You might have accidentally passed 'get' instead of 'set'. ` +
                    `'set' should accept state updates, while 'get' is for accessing state.`,
            );
        }

        const sliceNameCapitalized = (name.charAt(0).toUpperCase() +
            name.slice(1)) as Capitalize<TSliceName>;
        const initialStateValue = initialState ?? {
            [name]: {
                status: "idle",
                error: null,
                data: null,
            },
        };

        const setPending = () => {
            const pending = {
                [name]: {
                    status: "pending",
                    error: null,
                    data: null,
                },
            } as TStateAsyncSlice<TSliceName, TData, TError>;

            set(pending, false, `${name}/pending`);
        };

        const setResolved = (data: TData) => {
            const resolved = {
                [name]: {
                    status: "resolved",
                    error: null,
                    data: data,
                },
            } as TStateAsyncSlice<TSliceName, TData, TError>;

            set(resolved, false, `${name}/resolved`);
        };

        const setRejected = (error: TError) => {
            const rejected = {
                [name]: {
                    status: "rejected",
                    error: error,
                    data: null,
                },
            } as TStateAsyncSlice<TSliceName, TData, TError>;

            set(rejected, false, `${name}/rejected`);
        };

        const run = async (...args: TParams): Promise<TData | undefined> => {
            setPending();

            try {
                const data = await asyncFn(...args);
                setResolved(data);
                return data;
            } catch (error) {
                const currentError = error as TError;
                setRejected(currentError);
                if (rethrowError === true) {
                    throw currentError;
                }
                return undefined;
            }
        };

        return {
            ...initialStateValue,
            [`run${sliceNameCapitalized}`]: run,
        } as TAsyncSlice<TSliceName, TData, TError, TParams>;
    };
